if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.lightboxBound = false;
        this.preferredMediaId = null;
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onNavClick = this.onNavClick.bind(this);
        this.stopInteractivePointerUp = this.stopInteractivePointerUp.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onTrapFocusIn = this.onTrapFocusIn.bind(this);
        this.onTrapFocusOut = this.onTrapFocusOut.bind(this);
        this.onTrapKeyDown = this.onTrapKeyDown.bind(this);
      }

       connectedCallback() {
        if (typeof super.connectedCallback === 'function') {
          super.connectedCallback();
        }
        this.bindLightboxControls();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeyDown);
        this.teardownLightboxTrap();
        requestAnimationFrame(() => {
          if (this.isConnected) return;
          this.unsubscribeVariantChange?.();
          this.unsubscribeVariantChange = undefined;
          this.mediaObserver?.disconnect();
          this.mediaObserver = undefined;
          this.lightboxBound = false;
        });
      }

      bindLightboxControls() {
        if (this.lightboxBound) return;

        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');
        this.counter = this.querySelector('.product-media-modal__counter');
        this.counterCurrent = this.querySelector('.product-media-modal__counter-current');
        this.counterTotal = this.querySelector('.product-media-modal__counter-total');
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.mediaContent = this.querySelector('.product-media-modal__content');

        if (!this.dialog || !this.mediaContent) return;

        this.lightboxBound = true;

        this.prevButton?.addEventListener('click', this.onNavClick);
        this.nextButton?.addEventListener('click', this.onNavClick);
        this.dialog.addEventListener('pointerup', this.stopInteractivePointerUp);

        this.mediaObserver = new MutationObserver(() => {
          if (!this.hasAttribute('open')) return;
          this.refreshActiveMedia(this.preferredMediaId);
        });
        this.mediaObserver.observe(this.mediaContent, { childList: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.installLightboxTrap(true);
        document.removeEventListener('keydown', this.onKeyDown);
        document.addEventListener('keydown', this.onKeyDown);
      }

      hide() {
        document.removeEventListener('keydown', this.onKeyDown);
        this.teardownLightboxTrap();
        super.hide();
      }

      onVariantChange({ data } = {}) {
        const sectionId = data?.sectionId;
        if (sectionId && this.id !== `ProductModal-${sectionId}`) return;

        this.preferredMediaId = data?.variant?.featured_media?.id
          ? String(data.variant.featured_media.id)
          : this.preferredMediaId;

        if (this.hasAttribute('open')) {
          this.refreshActiveMedia(this.preferredMediaId);
        }
      }

      stopInteractivePointerUp(event) {
        if (
          event.target.closest(
            'img, video, iframe, model-viewer, deferred-media, product-model, button, .product-media-modal__counter, .product-media-modal__content > .active'
          )
        ) {
          event.stopPropagation();
        }
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.target.closest('input, textarea, select, video, model-viewer, iframe')) return;

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.cycle(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.cycle(-1);
        }
      }

      onNavClick(event) {
        event.preventDefault();
        this.cycle(event.currentTarget.name === 'next' ? 1 : -1);
      }

      getAllMediaItems() {
        const content = this.mediaContent || this.querySelector('.product-media-modal__content');
        if (!content) return [];
        return Array.from(content.querySelectorAll(':scope > [data-media-id]'));
      }

      getMediaItems() {
        return this.getAllMediaItems().filter((element, index) => {
          return !element.classList.contains('product__media-item--variant') || index === 0;
        });
      }

      resolveMedia(preferredId) {
        const items = this.getMediaItems();
        if (!items.length) return null;

        if (preferredId) {
          const match = items.find((element) => String(element.getAttribute('data-media-id')) === String(preferredId));
          if (match) return match;
        }

        return items[0];
      }

      cycle(direction) {
        const items = this.getMediaItems();
        if (items.length < 2) return;

        const currentIndex = items.findIndex((element) => element.classList.contains('active'));
        const nextIndex = (Math.max(currentIndex, 0) + direction + items.length) % items.length;
        this.activateMedia(items[nextIndex]);
        this.installLightboxTrap(false);
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        this.activateMedia(this.resolveMedia(mediaId));
      }

      refreshActiveMedia(preferredId) {
        this.activateMedia(this.resolveMedia(preferredId));
        if (this.hasAttribute('open')) this.installLightboxTrap(false);
      }

      activateMedia(activeMedia) {
        const items = this.getMediaItems();
        const allMedia = this.getAllMediaItems();
        if (!activeMedia || !items.length) return;

        window.pauseAllMedia();

        allMedia.forEach((element) => {
          const isActive = element === activeMedia;
          element.classList.toggle('active', isActive);
          this.setMediaInteractive(element, isActive);
        });

        this.updatePosition(items.indexOf(activeMedia), items.length);
      }

      setMediaInteractive(element, isActive) {
        element.toggleAttribute('inert', !isActive);
        element.toggleAttribute('aria-hidden', !isActive);

        element.querySelectorAll('button').forEach((button) => {
          button.disabled = !isActive;
          if (isActive) {
            button.removeAttribute('tabindex');
          } else {
            button.setAttribute('tabindex', '-1');
          }
        });

        element.querySelectorAll('iframe, video, model-viewer, a[href]').forEach((node) => {
          if (isActive) {
            const stored = node.getAttribute('data-lightbox-tabindex');
            if (stored === null || stored === '') {
              node.removeAttribute('tabindex');
            } else {
              node.setAttribute('tabindex', stored);
            }
            node.removeAttribute('data-lightbox-tabindex');
          } else if (!node.hasAttribute('data-lightbox-tabindex')) {
            node.setAttribute('data-lightbox-tabindex', node.getAttribute('tabindex') ?? '');
            node.setAttribute('tabindex', '-1');
          }
        });
      }

      updatePosition(index, total) {
        const current = index >= 0 ? index + 1 : 1;
        const hasNav = total > 1;

        if (this.counterCurrent) this.counterCurrent.textContent = String(current);
        if (this.counterTotal) this.counterTotal.textContent = String(total);
        this.counter?.toggleAttribute('hidden', total < 1);

        [this.prevButton, this.nextButton].forEach((button) => {
          if (!button) return;
          button.disabled = !hasNav;
          button.toggleAttribute('hidden', !hasNav);
          button.setAttribute('aria-hidden', hasNav ? 'false' : 'true');
          if (hasNav) {
            button.removeAttribute('tabindex');
          } else {
            button.setAttribute('tabindex', '-1');
          }
        });
      }

      getLightboxFocusableElements() {
        return Array.from(
          this.querySelectorAll(
            "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
          )
        ).filter((element) => {
          if (element.disabled || element.hidden) return false;
          if (element.closest('[inert], [hidden]')) return false;
          if (element.getAttribute('tabindex') === '-1') return false;
          const media = element.closest('.product-media-modal__content > [data-media-id]');
          if (media && !media.classList.contains('active')) return false;
          return true;
        });
      }

      onTrapFocusIn(event) {
        const elements = this.getLightboxFocusableElements();
        const first = elements[0];
        const last = elements[elements.length - 1];
        const dialog = this.dialog || this.querySelector('[role="dialog"]');
        if (
          event.target !== this &&
          event.target !== dialog &&
          event.target !== last &&
          event.target !== first
        ) {
          return;
        }
        document.addEventListener('keydown', this.onTrapKeyDown);
      }

      onTrapFocusOut() {
        document.removeEventListener('keydown', this.onTrapKeyDown);
      }

      onTrapKeyDown(event) {
        if (event.code.toUpperCase() !== 'TAB') return;
        const elements = this.getLightboxFocusableElements();
        const first = elements[0];
        const last = elements[elements.length - 1];
        const dialog = this.querySelector('[role="dialog"]');
        if (!first || !last) return;

        if ((event.target === last || event.target === dialog) && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        if ((event.target === dialog || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      }

      installLightboxTrap(stealFocus) {
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
        this.teardownLightboxTrap();

        document.addEventListener('focusout', this.onTrapFocusOut);
        document.addEventListener('focusin', this.onTrapFocusIn);

        const dialog = this.querySelector('[role="dialog"]');
        const current = document.activeElement;
        const focusable = this.getLightboxFocusableElements();
        const keepCurrent = !stealFocus && current && this.contains(current) && focusable.includes(current);
        (keepCurrent ? current : dialog)?.focus();
      }

      teardownLightboxTrap() {
        document.removeEventListener('focusin', this.onTrapFocusIn);
        document.removeEventListener('focusout', this.onTrapFocusOut);
        document.removeEventListener('keydown', this.onTrapKeyDown);
      }
    }
  );
}
